/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { UseQueryResult } from '@tanstack/react-query';
import { Form, Input } from 'antd';
import { FocusEventHandler, useEffect, useState } from 'react';

import { ApiError, NodeResponse } from '@/client';
import { useMessage } from '@/hooks/message';
import { useUpdateNodeMutation } from '@/hooks/mutation/useUpdateNodeMutation';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { tokens } from '@/theme';

export type NodeDetailsDescriptionProps = {
  nodeId: string;
  nodeQuery: UseQueryResult<NodeResponse, ApiError>;
  nodeData: NodeResponse;
};

export function NodeDetailsDescription({ nodeId, nodeQuery, nodeData }: NodeDetailsDescriptionProps) {
  const { t } = useMimicTranslation('node');
  const [form] = Form.useForm();
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const [message] = useMessage();

  const canUpdateNodeDescription = useAuthorization('editor');

  useEffect(() => {
    if (isDescriptionFocused) return;
    form.setFieldValue('description', nodeQuery.data?.data.description);
  }, [isDescriptionFocused, nodeQuery.data?.data, form]);

  const onDescriptionBlur: FocusEventHandler<HTMLTextAreaElement> = (event) => {
    setIsDescriptionFocused(false);
    if (event.target.value === null || event.target.value === undefined) return;
    const newDescription = event.target.value.trim();
    if (newDescription === nodeQuery.data!.data.description) return;
    mutate({ description: newDescription });
  };

  const onDescriptionFocus: FocusEventHandler<HTMLTextAreaElement> = () => {
    setIsDescriptionFocused(true);
  };

  const { mutate } = useUpdateNodeMutation({
    nodeId,
    onSuccess: () => message.success(t('feedback.nodeUpdated')),
    onError: () => message.error(t('feedback.updateFailed')),
  });

  return (
    <Form form={form} initialValues={nodeData.data} layout="vertical">
      <Form.Item name="description">
        <Input.TextArea
          allowClear
          rows={4}
          onBlur={onDescriptionBlur}
          onFocus={onDescriptionFocus}
          placeholder={t('node_description_placeholder')}
          count={{ show: true, max: 280 }}
          maxLength={280}
          disabled={!canUpdateNodeDescription}
          style={{
            backgroundColor: tokens.color.surface.surfaceHigh,
          }}
        />
      </Form.Item>
    </Form>
  );
}
